import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import * as R from 'ramda';
import getHistoryListDefaultHOCEnhance from './../../main/HistoryListDefaultHOC';
import HistoryList from '../../main/HistoryList';
import { useFunctionAsState } from '../../utilsReact';
import PDFView from '../PDFView';
import { Button, Icon } from '@mui/material';
import Box from '@mui/material/Box';
import { withProps } from 'react-recompose';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryListSelectCmp from '../../main/HistoryListSelectCmp';
import TabsCD from '../../component/TabsCD';
import { store } from '../store';
import { useXProps } from '../../xprops';
import { MenuRHF } from '@cd/rhf';
import ApplyPaymentToInvoices from './ApplyPaymentToInvoices';
import DeletePayment from './DeletePayment';
import DeleteInvoice from './DeleteInvoice';
import { ROUTE_BASE } from './vars';
import TabMenuGroupCD from '../../component/TabMenuGroupCD';

const FINAL_ROUTE_BASE = `${ROUTE_BASE}/clients`;
const PAID_VERB = 'paid';
const OUTSTANDING_VERB = 'outstanding';

const TABS = [
	{ name: 'NOT PAID', route: OUTSTANDING_VERB },
	{ name: 'PAID', route: PAID_VERB }
];

const actionStyle = {
	display: 'flex',
	alignItems: 'center'
};

const verticalButtonsStyle = {
	display: 'flex',
	flexDirection: 'column'
};

const withActionComponent = ({ data, dispatch }) => {
	function onViewPDF(invoiceID) {
		dispatch({
			type: 'open dialog',
			dlgCmp: <PDFView url={`/api/invoice/view/INVOICE_${invoiceID}.pdf`} />
		});
	}

	return withProps(props => ({
		...props,
		onSelected: item => {},
		onIcon: ({ is_paid, icon }, isGroup) =>
			!isGroup
				? <Icon>{icon}</Icon>
				: is_paid
					? <CheckCircleOutlineIcon />
					: <HighlightOffIcon style={{ color: 'red' }} />,
		onActionCmp: item => (
			<Box component="div" sx={actionStyle}>
				<Box component="div" sx={verticalButtonsStyle}>
					{item.is_paid && (
						<Button
							style={{
								marginRight: '4px',
								marginBottom: '4px'
							}}
							color="primary"
							variant="outlined"
							size="small"
							onClick={() =>
								dispatch({
									type: 'open dialog',
									dlgCmp: (
										<PDFView
											url={`/api/receipt/view/RECEIPT_${item.transaction_id}.pdf`}
										/>
									)
								})
							}>
							RECEIPT
						</Button>
					)}
					<Button
						color="secondary"
						style={{ marginRight: '4px' }}
						variant="outlined"
						size="small"
						onClick={() => onViewPDF(item.id)}>
						PDF
					</Button>
				</Box>
				<div>${item.amount}</div>
			</Box>
		)
	}));
};

const getVerbData = ({ dispatch, selected }, verb) =>
	R.prop(verb, {
		[PAID_VERB]: {
			selectedPropName: 'transaction_id',
			noDataMessage: 'NO TRANSACTIONS',
			menuConfig: [
				{
					title: 'DELETE TRANSACTION',
					name: 'remove_payment',
					fn: () => {
						dispatch({
							type: 'open dialog',
							dlgCmp: <DeletePayment selected={selected} />
						});
					}
				}
			]
		},
		[OUTSTANDING_VERB]: {
			selectedPropName: 'id',
			noDataMessage: 'NO OUTSTANDING PAYMENTS',
			menuConfig: [
				{
					title: 'PAY',
					name: 'pay',
					fn: () => {
						dispatch({
							type: 'open dialog',
							dlgCmp: <ApplyPaymentToInvoices selected={selected} />
						});
					}
				},
				{
					title: 'DELETE INVOICE',
					name: 'delete_invoice',
					fn: () => {
						dispatch({
							type: 'open dialog',
							dlgCmp: <DeleteInvoice selected={selected} />
						});
					}
				}
			]
		}
	});

const ClientInvoices = ({ data, match }) => {
	const [InvoiceList, setInvoiceList] = useFunctionAsState(null);
	const [selected, setSelected] = useState([]);
	const { dispatch } = useContext(store);
	const { childRoute } = useXProps();
	const { clientID, verb } = (match && match.params) || 0;
	const history = useHistory();

	const { noDataMessage, menuConfig, selectedPropName } = getVerbData(
		{ dispatch, selected },
		verb
	);

	useEffect(() => {
		if (data) {
			const InvoiceList = R.compose(
				getHistoryListDefaultHOCEnhance({
					store,
					method: 'GET',
					url: `/get/client/invoices/${clientID}/${verb}`,
					options: { reactQuery: { queryName: 'getClientInvoices' } }
				}),
				withActionComponent({ data, dispatch })
			)(HistoryList);

			setInvoiceList(InvoiceList);
			setSelected([]);
		}
	}, [data, verb]);

	function onTabChange(tabValue) {
		history.replace(`${FINAL_ROUTE_BASE}/${clientID}/invoices/${tabValue}`);
	}

	const setSelectedItems = R.compose(
		R.tap(setSelected),
		R.ifElse(
			() => verb === PAID_VERB,
			R.chain(([header, ...rest]) => (header.selected ? rest : [])),
			R.filter(R.propEq('selected', true))
		)
	);

	return (
		<>
			<TabMenuGroupCD>
				<TabsCD
					childRoute={childRoute}
					tabs={TABS}
					dark={true}
					onTabChange={onTabChange}
					initialValue={verb}
				/>
				<MenuRHF disabled={selected.length === 0} menuConfig={menuConfig} />
			</TabMenuGroupCD>

			{InvoiceList && (
				<InvoiceList
					noDataMessage={noDataMessage}
					total
					SelectCmp={
						<HistoryListSelectCmp
							selectedPropName={selectedPropName}
							onSelected={setSelectedItems}
						/>
					}
				/>
			)}

			{verb === OUTSTANDING_VERB && !R.isEmpty(selected) && (
				<ApplyPaymentToInvoices selected={selected} />
			)}
		</>
	);
};

export default ClientInvoices;
